/**
 * WordPress REST API Client
 *
 * Enterprise-grade API client with:
 * - Automatic nonce handling
 * - Retry logic with exponential backoff
 * - Request/response interceptors
 * - Custom error handling
 * - Type-safe responses
 *
 * @package WooAIChatbot
 */
/**
 * Custom API Error class with enhanced error information
 */
export class ApiError extends Error {
    constructor(message, statusCode, code, data) {
        super(message);
        Object.defineProperty(this, "statusCode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: statusCode
        });
        Object.defineProperty(this, "code", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: code
        });
        Object.defineProperty(this, "data", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: data
        });
        this.name = 'ApiError';
        Object.setPrototypeOf(this, ApiError.prototype);
    }
    /**
     * Check if error is a network error
     */
    isNetworkError() {
        return !this.statusCode || this.statusCode === 0;
    }
    /**
     * Check if error is a client error (4xx)
     */
    isClientError() {
        return !!this.statusCode && this.statusCode >= 400 && this.statusCode < 500;
    }
    /**
     * Check if error is a server error (5xx)
     */
    isServerError() {
        return !!this.statusCode && this.statusCode >= 500;
    }
    /**
     * Check if error is retryable
     */
    isRetryable() {
        // Retry on network errors, 408 (timeout), 429 (rate limit), 5xx (server errors)
        if (this.isNetworkError())
            return true;
        if (this.statusCode === 408 || this.statusCode === 429)
            return true;
        if (this.isServerError())
            return true;
        return false;
    }
}
/**
 * WordPress REST API Client
 */
class ApiClient {
    constructor() {
        Object.defineProperty(this, "baseUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "nonce", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "nonceRefreshPromise", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "requestInterceptors", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "responseInterceptors", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "errorInterceptors", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "defaultRetryCount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 3
        });
        Object.defineProperty(this, "defaultRetryDelay", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 1000
        }); // 1 second
        this.baseUrl = this.detectApiUrl();
        this.nonce = this.detectNonce();
    }
    /**
     * Detect WordPress REST API URL
     */
    detectApiUrl() {
        const globals = window.wooAiChatbot ?? window.wooAIChatbot;
        // Priority 1: window.wooAiChatbot.apiUrl (full URL provided by PHP)
        if (globals?.apiUrl) {
            return globals.apiUrl;
        }
        // Priority 2: Use absolute URL with origin to avoid path resolution issues
        // When on /wp-admin/ pages, relative /wp-json resolves incorrectly
        const origin = window.location.origin;
        return `${origin}/wp-json/woo-ai/v1/`;
    }
    /**
     * Detect WordPress nonce
     */
    detectNonce() {
        const globals = window.wooAiChatbot ?? window.wooAIChatbot;
        return globals?.nonce || '';
    }
    /**
     * Build full URL with query parameters
     */
    buildUrl(endpoint, params) {
        const url = new URL(endpoint, this.baseUrl);
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    if (Array.isArray(value)) {
                        value.forEach(v => url.searchParams.append(key, String(v)));
                    }
                    else {
                        url.searchParams.append(key, String(value));
                    }
                }
            });
        }
        return url.toString();
    }
    /**
     * Sleep utility for retry delays
     */
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    /**
     * Calculate exponential backoff delay
     */
    calculateBackoff(retryCount, baseDelay) {
        return baseDelay * Math.pow(2, retryCount - 1);
    }
    /**
     * Apply request interceptors
     */
    async applyRequestInterceptors(config) {
        let modifiedConfig = config;
        for (const interceptor of this.requestInterceptors) {
            modifiedConfig = await interceptor(modifiedConfig);
        }
        return modifiedConfig;
    }
    /**
     * Apply response interceptors
     */
    async applyResponseInterceptors(response) {
        let modifiedResponse = response;
        for (const interceptor of this.responseInterceptors) {
            modifiedResponse = await interceptor(modifiedResponse);
        }
        return modifiedResponse;
    }
    /**
     * Apply error interceptors
     */
    async applyErrorInterceptors(error) {
        for (const interceptor of this.errorInterceptors) {
            await interceptor(error);
        }
    }
    /**
     * Make HTTP request with retry logic
     */
    async request(endpoint, config = {}) {
        const { params, retry = true, retryCount = 0, maxRetries = this.defaultRetryCount, retryDelay = this.defaultRetryDelay, ...fetchOptions } = config;
        // Apply request interceptors
        const modifiedConfig = await this.applyRequestInterceptors({
            ...fetchOptions,
            params,
            retry,
            retryCount,
            maxRetries,
            retryDelay,
        });
        const url = this.buildUrl(endpoint, modifiedConfig.params);
        try {
            const response = await fetch(url, {
                ...modifiedConfig,
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': this.nonce,
                    ...modifiedConfig.headers,
                },
            });
            // Handle non-OK responses
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({
                    message: response.statusText || 'Request failed',
                    code: 'unknown_error',
                }));
                const apiError = new ApiError(errorData.message || 'Request failed', response.status, errorData.code, errorData.data);
                // Apply error interceptors
                await this.applyErrorInterceptors(apiError);
                // Auto-refresh nonce on 403 nonce errors (handles page caching).
                if (response.status === 403 && (errorData.code === 'invalid_nonce' || errorData.code === 'missing_nonce') && retryCount === 0) {
                    await this.refreshNonce();
                    return this.request(endpoint, {
                        ...config,
                        retryCount: retryCount + 1,
                    });
                }
                // Retry logic
                if (retry && apiError.isRetryable() && retryCount < maxRetries) {
                    const backoffDelay = this.calculateBackoff(retryCount + 1, retryDelay);
                    await this.sleep(backoffDelay);
                    return this.request(endpoint, {
                        ...config,
                        retryCount: retryCount + 1,
                    });
                }
                throw apiError;
            }
            // Parse response
            const data = await response.json();
            // Apply response interceptors
            const modifiedData = await this.applyResponseInterceptors(data);
            return modifiedData;
        }
        catch (error) {
            // Handle network errors
            if (error instanceof ApiError) {
                throw error;
            }
            const apiError = new ApiError(error instanceof Error ? error.message : 'Network error', 0, 'network_error');
            // Apply error interceptors
            await this.applyErrorInterceptors(apiError);
            // Retry on network errors
            if (retry && retryCount < maxRetries) {
                const backoffDelay = this.calculateBackoff(retryCount + 1, retryDelay);
                await this.sleep(backoffDelay);
                return this.request(endpoint, {
                    ...config,
                    retryCount: retryCount + 1,
                });
            }
            throw apiError;
        }
    }
    /**
     * Add request interceptor
     */
    addRequestInterceptor(interceptor) {
        this.requestInterceptors.push(interceptor);
    }
    /**
     * Add response interceptor
     */
    addResponseInterceptor(interceptor) {
        this.responseInterceptors.push(interceptor);
    }
    /**
     * Add error interceptor
     */
    addErrorInterceptor(interceptor) {
        this.errorInterceptors.push(interceptor);
    }
    /**
     * Remove all interceptors
     */
    clearInterceptors() {
        this.requestInterceptors = [];
        this.responseInterceptors = [];
        this.errorInterceptors = [];
    }
    /**
     * Refresh nonce from server (bypasses page cache)
     * Deduplicates concurrent refresh calls.
     */
    async refreshNonce() {
        if (this.nonceRefreshPromise) {
            return this.nonceRefreshPromise;
        }
        this.nonceRefreshPromise = (async () => {
            try {
                const url = this.buildUrl('chat/nonce');
                const response = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
                if (response.ok) {
                    const data = await response.json();
                    if (data?.nonce) {
                        this.nonce = data.nonce;
                        return data.nonce;
                    }
                }
            }
            catch {
                // Nonce refresh failed — keep existing nonce.
            }
            finally {
                this.nonceRefreshPromise = null;
            }
            return this.nonce;
        })();
        return this.nonceRefreshPromise;
    }
    /**
     * Update nonce (useful for long-running sessions)
     */
    updateNonce(nonce) {
        this.nonce = nonce;
    }
    /**
     * Update base URL
     */
    updateBaseUrl(url) {
        this.baseUrl = url;
    }
    /**
     * GET request
     */
    get(endpoint, params, config) {
        return this.request(endpoint, {
            ...config,
            method: 'GET',
            params,
        });
    }
    /**
     * POST request
     */
    post(endpoint, data, config) {
        return this.request(endpoint, {
            ...config,
            method: 'POST',
            body: data ? JSON.stringify(data) : undefined,
        });
    }
    /**
     * PUT request
     */
    put(endpoint, data, config) {
        return this.request(endpoint, {
            ...config,
            method: 'PUT',
            body: data ? JSON.stringify(data) : undefined,
        });
    }
    /**
     * PATCH request
     */
    patch(endpoint, data, config) {
        return this.request(endpoint, {
            ...config,
            method: 'PATCH',
            body: data ? JSON.stringify(data) : undefined,
        });
    }
    /**
     * DELETE request
     */
    delete(endpoint, config) {
        return this.request(endpoint, {
            ...config,
            method: 'DELETE',
        });
    }
}
/**
 * Singleton instance
 */
const apiClient = new ApiClient();
/**
 * Export singleton instance as default
 */
export default apiClient;
/**
 * Export class for testing and advanced usage
 */
export { ApiClient };
